// src/services/address.service.ts
import { db } from '../config/firebase';
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  Timestamp,
  writeBatch,
  QueryConstraint,
} from 'firebase/firestore';
import {
  WorkloadAddress,
  AddressStatus,
  CreateAddressDto,
  UpdateAddressDto,
  UpdateResidentInfoDto,
  AddressFilters,
  AddressVisit,
  StatusChange,
} from '../types/address.types';
import { workloadService } from './workload.service';
import { LocationCapture, AccountabilityRecord } from './location.service';


function getAddressesCollection(workloadId: string) {
  return collection(db, 'workloads', workloadId, 'addresses');
}

function getAddressDoc(workloadId: string, addressId: string) {
  return doc(db, 'workloads', workloadId, 'addresses', addressId);
}


export async function createAddress(
  workloadId: string,
  data: CreateAddressDto
): Promise<WorkloadAddress> {
  const now = Timestamp.now();

  const addressData = {
    workloadId,
    street: data.street,
    unit: data.unit || null,
    city: data.city,
    state: data.state,
    zipCode: data.zipCode,
    fullAddress: buildFullAddress(data),
    location: {
      lat: data.lat ?? 0,
      lng: data.lng ?? 0,
      accuracy: null,
      geocodedAt: now,
    },
    status: 'pending' as AddressStatus,
    statusHistory: [],
    attemptCount: 0,
    visits: [],
    lastVisitedAt: null,
    lastVisitedBy: null,
    lastVisitedByName: null,
    leadId: null,
    surveyResponseId: null,
    residentName: data.residentName || null,
    residentPhone: data.residentPhone || null,
    residentEmail: data.residentEmail || null,
    tags: data.tags || [],
    notes: data.notes || null,
    syncStatus: 'synced' as const,
    createdAt: now,
    updatedAt: now,
  };

  const colRef = getAddressesCollection(workloadId);
  const docRef = await addDoc(colRef, addressData);

  // Update workload progress
  await updateWorkloadAddressCount(workloadId, 1);

  return {
    id: docRef.id,
    ...addressData,
  } as WorkloadAddress;
}

export async function batchCreateAddresses(
  workloadId: string,
  addresses: CreateAddressDto[],
  onProgress?: (completed: number, total: number) => void
): Promise<{ created: number; errors: string[] }> {
  if (addresses.length === 0) {
    return { created: 0, errors: ['No addresses provided'] };
  }

  const errors: string[] = [];
  let created = 0;
  const batchSize = 500;

  // Process in batches of 500 (Firestore limit)
  for (let i = 0; i < addresses.length; i += batchSize) {
    const batch = writeBatch(db);
    const batchAddresses = addresses.slice(i, i + batchSize);
    const now = Timestamp.now();

    for (const data of batchAddresses) {
      try {
        const colRef = getAddressesCollection(workloadId);
        const docRef = doc(colRef);

        const addressData = {
          workloadId,
          street: data.street,
          unit: data.unit || null,
          city: data.city,
          state: data.state,
          zipCode: data.zipCode,
          fullAddress: buildFullAddress(data),
          location: {
            lat: data.location.lat,
            lng: data.location.lng,
            accuracy: data.location.accuracy ?? null,
            geocodedAt: data.location.geocodedAt || now,
          },
          status: 'pending' as AddressStatus,
          statusHistory: [],
          attemptCount: 0,
          visits: [],
          lastVisitedAt: null,
          lastVisitedBy: null,
          lastVisitedByName: null,
          leadId: null,
          surveyResponseId: null,
          tags: data.tags || [],
          notes: null,
          syncStatus: 'synced' as const,
          createdAt: now,
          updatedAt: now,
        };

        batch.set(docRef, addressData);
        created++;
      } catch (error: any) {
        console.error('Error adding address to batch:', error);
        errors.push(`${data.street}: ${error.message}`);
      }
    }

    try {
      await batch.commit();
    } catch (commitError: any) {
      console.error('Batch commit failed:', commitError);
      errors.push(`Batch commit failed: ${commitError.message}`);
      // Reset created count for this batch since commit failed
      created -= batchAddresses.length;
    }
    onProgress?.(Math.min(i + batchSize, addresses.length), addresses.length);
  }

  // Update workload progress
  try {
    await updateWorkloadAddressCount(workloadId, created);
  } catch (updateError: any) {
    console.error('Failed to update workload address count:', updateError);
  }

  return { created, errors };
}


export async function getAddress(
  workloadId: string,
  addressId: string
): Promise<WorkloadAddress | null> {
  const docRef = getAddressDoc(workloadId, addressId);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    return null;
  }

  return {
    id: docSnap.id,
    ...docSnap.data(),
  } as WorkloadAddress;
}

export async function getWorkloadAddresses(
  workloadId: string,
  filters?: AddressFilters
): Promise<WorkloadAddress[]> {
  const constraints: QueryConstraint[] = [];

  if (filters?.status && filters.status.length > 0) {
    constraints.push(where('status', 'in', filters.status));
  }

  if (filters?.hasTag) {
    constraints.push(where('tags', 'array-contains', filters.hasTag));
  }

  const q = query(getAddressesCollection(workloadId), ...constraints);
  const snapshot = await getDocs(q);

  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as WorkloadAddress[];
}

export async function getWorkloadAddressesPaginated(
  workloadId: string,
  pageSize: number = 50,
  filters?: AddressFilters
): Promise<WorkloadAddress[]> {
  const constraints: QueryConstraint[] = [limit(pageSize)];

  if (filters?.status && filters.status.length > 0) {
    constraints.push(where('status', 'in', filters.status));
  }

  const q = query(getAddressesCollection(workloadId), ...constraints);
  const snapshot = await getDocs(q);

  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as WorkloadAddress[];
}

export async function getAddressCount(workloadId: string): Promise<number> {
  const snapshot = await getDocs(getAddressesCollection(workloadId));
  return snapshot.size;
}

export async function getAddressStats(workloadId: string): Promise<Record<AddressStatus, number>> {
  const addresses = await getWorkloadAddresses(workloadId);

  const stats: Record<AddressStatus, number> = {
    pending: 0,
    visited: 0,
    not_home: 0,
    refused: 0,
    completed: 0,
    discontinued: 0,
    inaccessible: 0,
  };

  addresses.forEach((address) => {
    stats[address.status]++;
  });

  return stats;
}


export async function updateAddress(
  workloadId: string,
  addressId: string,
  data: UpdateAddressDto
): Promise<void> {
  const docRef = getAddressDoc(workloadId, addressId);

  await updateDoc(docRef, {
    ...data,
    updatedAt: Timestamp.now(),
  });
}

export async function updateResidentInfo(
  workloadId: string,
  addressId: string,
  data: UpdateResidentInfoDto
): Promise<void> {
  const docRef = getAddressDoc(workloadId, addressId);

  const updates: Record<string, any> = {
    updatedAt: Timestamp.now(),
  };

  if (data.residentName !== undefined) updates.residentName = data.residentName || null;
  if (data.residentPhone !== undefined) updates.residentPhone = data.residentPhone || null;
  if (data.residentEmail !== undefined) updates.residentEmail = data.residentEmail || null;
  if (data.notes !== undefined) updates.notes = data.notes || null;

  await updateDoc(docRef, updates);
}

export async function updateAddressStatus(
  workloadId: string,
  addressId: string,
  status: AddressStatus,
  userId: string,
  userName: string,
  location?: LocationCapture,
  notes?: string
): Promise<void> {
  const docRef = getAddressDoc(workloadId, addressId);
  const existing = await getDoc(docRef);

  if (!existing.exists()) {
    throw new Error('Address not found');
  }

  const existingData = existing.data() as WorkloadAddress;
  const previousStatus = existingData.status;
  const now = Timestamp.now();

  // Create status change record
  const statusChange: StatusChange = {
    from: previousStatus,
    to: status,
    changedBy: userId,
    changedByName: userName,
    changedAt: now,
    notes: notes ?? null,
    location: location
      ? {
          lat: location.lat,
          lng: location.lng,
          accuracy: location.accuracy ?? null,
        }
      : null,
  };

  // Create visit record if this is an actual visit (not just status update)
  const visitStatuses: AddressStatus[] = ['visited', 'not_home', 'refused', 'completed', 'inaccessible'];
  const isVisit = visitStatuses.includes(status);

  const updates: Record<string, any> = {
    status,
    statusHistory: [...(existingData.statusHistory || []), statusChange],
    updatedAt: now,
  };

  if (isVisit) {
    const visit: AddressVisit = {
      id: `visit_${Date.now()}`,
      visitedAt: now,
      visitedBy: userId,
      visitedByName: userName,
      result: status,
      duration: null, // Can be calculated if tracking start/end
      notes: notes ?? null,
      location: location
        ? {
            lat: location.lat,
            lng: location.lng,
            accuracy: location.accuracy ?? null,
          }
        : null,
    };

    updates.visits = [...(existingData.visits || []), visit];
    updates.attemptCount = (existingData.attemptCount || 0) + 1;
    updates.lastVisitedAt = now;
    updates.lastVisitedBy = userId;
    updates.lastVisitedByName = userName;
  }

  await updateDoc(docRef, updates);

  // Update workload progress counters incrementally (fast - no full recalculation)
  await updateWorkloadProgressIncremental(workloadId, previousStatus, status);
}

export async function linkLeadToAddress(
  workloadId: string,
  addressId: string,
  leadId: string
): Promise<void> {
  const docRef = getAddressDoc(workloadId, addressId);

  await updateDoc(docRef, {
    leadId,
    updatedAt: Timestamp.now(),
  });
}

export async function linkSurveyResponseToAddress(
  workloadId: string,
  addressId: string,
  surveyResponseId: string
): Promise<void> {
  const docRef = getAddressDoc(workloadId, addressId);

  await updateDoc(docRef, {
    surveyResponseId,
    updatedAt: Timestamp.now(),
  });
}

export async function addNoteToAddress(
  workloadId: string,
  addressId: string,
  note: string
): Promise<void> {
  const docRef = getAddressDoc(workloadId, addressId);
  const existing = await getDoc(docRef);

  if (!existing.exists()) {
    throw new Error('Address not found');
  }

  const existingNotes = existing.data().notes || '';
  const separator = existingNotes ? '\n---\n' : '';
  const newNotes = existingNotes + separator + `[${new Date().toISOString()}] ${note}`;

  await updateDoc(docRef, {
    notes: newNotes,
    updatedAt: Timestamp.now(),
  });
}

export async function addTagToAddress(
  workloadId: string,
  addressId: string,
  tag: { source: string; name: string; value: string }
): Promise<void> {
  const docRef = getAddressDoc(workloadId, addressId);
  const existing = await getDoc(docRef);

  if (!existing.exists()) {
    throw new Error('Address not found');
  }

  const existingTags = existing.data().tags || [];
  const tagWithId = {
    ...tag,
    id: `tag_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    appliedAt: Timestamp.now(),
  };

  await updateDoc(docRef, {
    tags: [...existingTags, tagWithId],
    updatedAt: Timestamp.now(),
  });
}


export async function batchUpdateStatus(
  workloadId: string,
  addressIds: string[],
  status: AddressStatus,
  userId: string,
  userName: string
): Promise<{ success: number; failed: number }> {
  const batch = writeBatch(db);
  const now = Timestamp.now();
  let success = 0;
  let failed = 0;

  for (const addressId of addressIds) {
    try {
      const docRef = getAddressDoc(workloadId, addressId);
      batch.update(docRef, {
        status,
        updatedAt: now,
      });
      success++;
    } catch {
      failed++;
    }
  }

  await batch.commit();
  await recalculateWorkloadProgress(workloadId);

  return { success, failed };
}

export async function batchDeleteAddresses(
  workloadId: string,
  addressIds: string[]
): Promise<{ deleted: number; failed: number }> {
  const batch = writeBatch(db);
  let deleted = 0;
  let failed = 0;

  for (const addressId of addressIds) {
    try {
      const docRef = getAddressDoc(workloadId, addressId);
      batch.delete(docRef);
      deleted++;
    } catch {
      failed++;
    }
  }

  await batch.commit();
  await updateWorkloadAddressCount(workloadId, -deleted);

  return { deleted, failed };
}


export async function deleteAddress(workloadId: string, addressId: string): Promise<void> {
  const docRef = getAddressDoc(workloadId, addressId);
  await deleteDoc(docRef);
  await updateWorkloadAddressCount(workloadId, -1);
}

export async function deleteAllWorkloadAddresses(workloadId: string): Promise<number> {
  const addresses = await getWorkloadAddresses(workloadId);
  const batch = writeBatch(db);

  addresses.forEach((address) => {
    const docRef = getAddressDoc(workloadId, address.id);
    batch.delete(docRef);
  });

  await batch.commit();
  return addresses.length;
}


export function subscribeToWorkloadAddresses(
  workloadId: string,
  callback: (addresses: WorkloadAddress[]) => void
): () => void {
  return onSnapshot(getAddressesCollection(workloadId), (snapshot) => {
    const addresses = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as WorkloadAddress[];
    callback(addresses);
  });
}


function buildFullAddress(data: CreateAddressDto): string {
  const parts = [data.street];
  if (data.unit) parts.push(`Unit ${data.unit}`);
  parts.push(`${data.city}, ${data.state} ${data.zipCode}`);
  return parts.join(', ');
}

async function updateWorkloadAddressCount(workloadId: string, delta: number): Promise<void> {
  const workload = await workloadService.get(workloadId);
  if (!workload) return;

  const newTotal = Math.max(0, workload.progress.totalAddresses + delta);
  const newPending = Math.max(0, workload.progress.pending + delta);

  await workloadService.updateProgress(workloadId, {
    totalAddresses: newTotal,
    pending: newPending,
    completionPercentage: newTotal > 0
      ? Math.round((workload.progress.completed / newTotal) * 100)
      : 0,
  });
}

async function recalculateWorkloadProgress(workloadId: string): Promise<void> {
  const stats = await getAddressStats(workloadId);
  const total = Object.values(stats).reduce((sum, count) => sum + count, 0);

  await workloadService.updateProgress(workloadId, {
    totalAddresses: total,
    pending: stats.pending,
    visited: stats.visited,
    notHome: stats.not_home,
    refused: stats.refused,
    completed: stats.completed,
    discontinued: stats.discontinued || 0,
    inaccessible: stats.inaccessible,
    completionPercentage: total > 0 ? Math.round((stats.completed / total) * 100) : 0,
    lastActivityAt: Timestamp.now(),
  });
}

// Incremental progress update - much faster than full recalculation
async function updateWorkloadProgressIncremental(
  workloadId: string,
  fromStatus: AddressStatus,
  toStatus: AddressStatus
): Promise<void> {
  if (fromStatus === toStatus) return; // No change

  const workload = await workloadService.get(workloadId);
  if (!workload) return;

  const progress = { ...workload.progress };

  // Decrement old status counter
  const fromKey = fromStatus === 'not_home' ? 'notHome' : fromStatus;
  if (fromKey in progress && typeof progress[fromKey as keyof typeof progress] === 'number') {
    (progress as any)[fromKey] = Math.max(0, ((progress as any)[fromKey] || 0) - 1);
  }

  // Increment new status counter
  const toKey = toStatus === 'not_home' ? 'notHome' : toStatus;
  if (toKey in progress && typeof progress[toKey as keyof typeof progress] === 'number') {
    (progress as any)[toKey] = ((progress as any)[toKey] || 0) + 1;
  }

  // Recalculate completion percentage
  const total = progress.totalAddresses || 0;
  progress.completionPercentage = total > 0
    ? Math.round((progress.completed / total) * 100)
    : 0;

  await workloadService.updateProgress(workloadId, {
    ...progress,
    lastActivityAt: Timestamp.now(),
  });
}

// Export service object
export const addressService = {
  create: createAddress,
  batchCreate: batchCreateAddresses,
  get: getAddress,
  getWorkloadAddresses,
  getPaginated: getWorkloadAddressesPaginated,
  getCount: getAddressCount,
  getStats: getAddressStats,
  update: updateAddress,
  updateResidentInfo,
  updateStatus: updateAddressStatus,
  linkLead: linkLeadToAddress,
  linkSurveyResponse: linkSurveyResponseToAddress,
  addNote: addNoteToAddress,
  addTag: addTagToAddress,
  batchUpdateStatus,
  batchDelete: batchDeleteAddresses,
  delete: deleteAddress,
  deleteAll: deleteAllWorkloadAddresses,
  subscribeToAddresses: subscribeToWorkloadAddresses,
};

export default addressService;
